import { useTranslation } from '../../i18n/LanguageContext'
import { useCountCutlery } from './useCountCutlery'
import type { CutleryType } from './useCutlery'
import { useTimer } from '../useTimer'
import { CutleryIcon } from './CutlerySVGs'
import { s } from './cutleryStyles'
import GamePlayHeader from '../GamePlayHeader'

const SLOT_COLORS: Record<CutleryType, { liner: string; border: string; accent: string }> = {
  spoon:    { liner: '#FFF8EE', border: '#E8C87A', accent: '#D4A017' },
  fork:     { liner: '#EDF6EE', border: '#8FC89A', accent: '#52B788' },
  knife:    { liner: '#F5F0EA', border: '#C0A882', accent: '#8B6347' },
  teaspoon: { liner: '#EEF3FF', border: '#94A8D8', accent: '#4A6FA5' },
}

const ALL_CUTLERY_TYPES: CutleryType[] = ['spoon', 'fork', 'knife', 'teaspoon']

/* ── Count Play Screen ────────────────────────────────────────── */
export function CountPlayScreen({ state, onAddPiece, onRemovePiece, onCheckAnswer, onBack }: {
  state:          ReturnType<typeof useCountCutlery>['state']
  onAddPiece:     (type: CutleryType) => void
  onRemovePiece:  (type: CutleryType) => void
  onCheckAnswer:  () => void
  onBack:         () => void
}) {
  const { t, language } = useTranslation()
  const c       = t.cutlery
  const elapsed = useTimer(state.startTime, state.endTime)

  // Progress: how many types are already correct (after a check)
  const correctCount = state.checkResult
    ? ALL_CUTLERY_TYPES.filter((t) => state.checkResult![t] === 'correct').length
    : 0
  const progress = state.gameComplete ? 1 : correctCount / ALL_CUTLERY_TYPES.length

  const hasAnyExtracted = ALL_CUTLERY_TYPES.some((t) => state.extracted[t] > 0)

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={c.modes.count.label}
        stats={[
          { value: state.moves, label: c.moves },
          { value: elapsed,     label: c.time },
        ]}
        progress={progress * 100}
        progressNow={correctCount}
        progressMax={ALL_CUTLERY_TYPES.length}
        onBack={onBack}
        backLabel={c.backToDashboard}
      />

      <main style={s.countMain}>
        {/* Comanda — always visible */}
        <div style={s.comandaCard}>
          <div style={s.comandaTitleRow}>
            <span style={s.comandaTitle}>{c.comandaTitle}</span>
            <span style={s.comandaDiners}>👥 {state.dinerCount}</span>
          </div>
          {state.order.map((item, i) => (
            <div key={i} style={s.comandaRow}>
              <span style={s.comandaQty}>{item.qty}×</span>
              <span style={s.comandaEmoji}>{item.dish.emoji}</span>
              <span style={s.comandaName}>{language === 'es' ? item.dish.es : item.dish.en}</span>
            </div>
          ))}
        </div>

        {/* Extracted pieces display */}
        <div style={s.extractedGrid}>
          {ALL_CUTLERY_TYPES.map((type) => {
            const count  = state.extracted[type]
            const result = state.checkResult?.[type] ?? null
            const isCorrect = result === 'correct'
            const isWrong   = result === 'wrong'
            return (
              <div
                key={type}
                className={isWrong ? 'wm-shake' : ''}
                style={{
                  ...s.extractedSlot,
                  borderColor: isCorrect ? '#52B788' : isWrong ? '#B5541A' : SLOT_COLORS[type].border,
                  background:  isCorrect ? '#F0FFF8' : isWrong ? '#FFF4F0' : SLOT_COLORS[type].liner,
                }}
              >
                <div style={s.extractedSlotHeader}>
                  <span style={{ ...s.extractedSlotLabel, color: SLOT_COLORS[type].accent }}>
                    {c.slots[type]}
                  </span>
                  <span style={{ ...s.extractedCount, color: isCorrect ? '#52B788' : isWrong ? '#B5541A' : SLOT_COLORS[type].accent }}>
                    {isCorrect && '✓ '}
                    {isWrong   && '✗ '}
                    {count}
                  </span>
                </div>
                <div style={s.extractedPieces}>
                  {Array.from({ length: count }).map((_, i) => (
                    <button
                      key={i}
                      onClick={() => onRemovePiece(type)}
                      style={s.extractedPiece}
                      title={`Devolver ${c.slots[type]}`}
                      aria-label={`Devolver ${c.slots[type]}`}
                    >
                      <CutleryIcon type={type} width={20} />
                    </button>
                  ))}
                  {count === 0 && (
                    <span style={s.extractedEmpty}>—</span>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        <p style={s.countInstruction}>{c.drawerTapInstruction}</p>

        {/* Drawer — tap to extract */}
        <div style={s.countDrawerFrame}>
          <div style={s.drawerInner}>
            {ALL_CUTLERY_TYPES.map((type) => (
              <button
                key={type}
                onClick={() => onAddPiece(type)}
                disabled={state.gameComplete}
                style={{
                  ...s.countDrawerSlot,
                  background:   SLOT_COLORS[type].liner,
                  borderColor:  SLOT_COLORS[type].border,
                }}
                aria-label={`Sacar ${c.slots[type]}`}
              >
                <CutleryIcon type={type} width={28} />
                <span style={{ ...s.countDrawerLabel, color: SLOT_COLORS[type].accent }}>
                  {c.slots[type]}
                </span>
                <span style={{ ...s.countDrawerPlus, color: SLOT_COLORS[type].accent }}>+</span>
              </button>
            ))}
          </div>
        </div>

        {/* Check button */}
        {!state.gameComplete && (
          <button
            onClick={onCheckAnswer}
            style={s.checkBtn}
            disabled={!hasAnyExtracted}
          >
            {c.checkBtn}
          </button>
        )}

        {/* Wrong feedback */}
        {state.checkResult && !state.gameComplete && (
          <p style={s.countFeedback}>{c.countWrongFeedback}</p>
        )}
      </main>
    </div>
  )
}
